<!-- Remove the test form overlay since we're auto-generating values -->
<!-- Keep the Layout Dialog component -->
@if (showLayoutDialog && (selectedStore$ | ngrxPush); as selectedStore) {
  <app-layout-dialog
    (copyLayouts)="onCopyLayouts($event)"
    (deleteLayout)="handleDeleteLayout($event)"
    (dialogClosed)="closeLayoutDialog()"
    (loadLayout)="handleLoadLayout($event)"
    (pasteLayouts)="onPasteAllLayouts()"
    (saveLayout)="handleSaveLayout($event, selectedStore)"
    [allowCopy]="canCopyLayoutsFromStore"
    [clipboardCount]="copiedLayoutNames.length"
    [currentLayoutName]="currentLayoutName"
    [hasClipboardLayouts]="copiedLayoutNames.length > 0"
    [isPasting]="isPastingLayouts"
    [savedLayouts]="savedLayouts">
  </app-layout-dialog>
}

<div class="preview-header">
  <h3>Label Controls</h3>
  <div class="preview-actions">
    <app-shared-button (click)="resetLabelDesigner()">
      <app-display-icon
        [colour]="iconColourEnum.red"
        [icon]="icons.deleteOutline">
      </app-display-icon>
      Clear Designer
    </app-shared-button>
    <app-shared-button (click)="setLabelAsDefault()">
      <app-display-icon
        [colour]="iconColourEnum.green"
        [icon]="icons.checkAll">
      </app-display-icon>
      Set As Default
    </app-shared-button>
    <app-shared-button (click)="openLayoutDialog()">
      <app-display-icon
        [colour]="iconColourEnum.green"
        [icon]="icons.contentSaveOutline">
      </app-display-icon>
      Save/Load Layouts
    </app-shared-button>
    <app-shared-button
      [disabled]="!singlePasteLayoutName || isPastingLayouts"
      (click)="onPasteOneLayout()">
      <app-display-icon
        [colour]="iconColourEnum.secondary"
        [icon]="icons.clipboardCheckOutline"
        [tooltip]="'Paste one copied layout into this store'">
      </app-display-icon>
      {{ isPastingLayouts ? 'Pasting…' : 'Paste Layout' }}
      @if (singlePasteLayoutName && !isPastingLayouts) {
        <span class="paste-count">({{ singlePasteLayoutName }})</span>
      }
    </app-shared-button>
    <app-shared-button (click)="generateRandomTestData()">
      <app-display-icon
        [colour]="iconColourEnum.yellow"
        [icon]="icons.pencil">
      </app-display-icon>
      Test Generation
    </app-shared-button>
    <app-shared-button (click)="testSingleLabelGeneration()">
      <app-display-icon
        [colour]="iconColourEnum.green"
        [icon]="icons.pencil">
      </app-display-icon>
      Test Single Label
    </app-shared-button>

  </div>
</div>
<div class="label-designer-container">
  <div class="sidebar left-sidebar">
    <div class="label-settings">
      <h3>Label Settings</h3>
      <div class="size-inputs">
        <div class="form-field improved-form-field">
          <label for="width-input">Width (mm)</label>
          <input
            (keyup)="updateLabelSize(false)"
            [(ngModel)]="labelWidthMm"
            id="width-input"
            max="500"
            min="10"
            type="number">
        </div>

        <div class="form-field improved-form-field">
          <label for="height-input">Height (mm)</label>
          <input
            (keyup)="updateLabelSize(false)"
            [(ngModel)]="labelHeightMm"
            id="height-input"
            max="500"
            min="10"
            type="number">
        </div>
        <div class="form-field improved-form-field">
          <label for="height-input">Currency Symbol</label>
          <input
            (keyup)="updateCurrencySymbol()"
            [(ngModel)]="currencySymbol"
            id="currency-symbol"
            type="text">
        </div>

        <!-- Add grid snap toggle -->
        <div class="grid-snap-toggle">
          <label class="toggle-label">
            <input (change)="toggleGridSnap()" [checked]="enableGridSnap" type="checkbox">
            <span class="toggle-text">Snap to Grid (1mm)</span>
          </label>
          @if (enableGridSnap) {
            <div class="grid-snap-hint">
              <span>Use arrow keys to move selected fields</span>
            </div>
          }
        </div>
      </div>
    </div>

    <hr class="divider">

    <!-- Variables list -->
    <div
      #variablesList="cdkDropList"
      [cdkDropListConnectedTo]="[labelList]"
      [cdkDropListData]="variables"
      cdkDropList
      class="variables-list">
      <h3>Available Fields</h3>
      <div class="variables-container">
        @for (variable of variables; track $index) {
          <div
            cdkDrag
            class="variable-item">
            {{ variable.name }}
          </div>
        }
      </div>
    </div>
  </div>

  <div class="main-content">
    <div #labelList="cdkDropList"
         (cdkDropListDropped)="onDrop($event)"
         [cdkDropListConnectedTo]="[variablesList]"
         [cdkDropListData]="labelItems"
         [cdkDropListSortingDisabled]="true"
         cdkDropList
         class="label-container">
      <canvas #labelCanvas
              (mousedown)="onCanvasMouseDown($event)"
              (mouseleave)="onCanvasMouseLeave()"
              (mousemove)="onCanvasMouseMove($event)"
              (mouseup)="onCanvasMouseUp($event)"></canvas>
      @if (hasUnsavedChanges) {
        <app-display-icon
          [colour]="iconColourEnum.yellow"
          [icon]="icons.contentSaveOutline"
          [tooltip]="'Unsaved Changes'">
        </app-display-icon>
      }

      <!-- Zoom controls -->
      <div class="zoom-controls">
        @if (zoomLevel !== 1) {
          <app-shared-button
            (click)="resetZoom()"
            [colour]="'clear'">
            <app-display-icon
              [colour]="iconColourEnum.yellow"
              [icon]="icons.sync"
              [tooltip]="'Reset Zoom'">
            </app-display-icon>
          </app-shared-button>
        }
        <app-shared-button
          [colour]="'clear'"
          [disabled]="zoomLevel === zoomLevels[0]">
          <app-display-icon
            (click)="zoomOut()"
            [colour]="iconColourEnum.primary"
            [icon]="icons.magnifyMinus">
          </app-display-icon>
        </app-shared-button>
        <div class="zoom-level">{{ (zoomLevel * 100) | number:'1.0-0' }}%</div>
        <app-shared-button
          [colour]="'clear'"
          [disabled]="zoomLevel === zoomLevels[zoomLevels.length-1]">
          <app-display-icon
            (click)="zoomIn()"
            [colour]="iconColourEnum.primary"
            [icon]="icons.magnifyPlus">
          </app-display-icon>
        </app-shared-button>
      </div>
    </div>


  </div>

  <div #rightSidenav [class.opened]="showFormatSidebar" class="sidebar right-sidebar">
    @if (selectedItem) {
      <div class="format-sidebar">
        <!-- Format header - shown for all item types -->
        <div class="format-header">
          <h3>Format {{ selectedItem.name }}</h3>
          <div class="header-actions">
            <app-shared-button (click)="deleteSelectedItem()" colour="clear">
              <app-display-icon
                [colour]="iconColourEnum.red"
                [icon]="icons.deleteOutline">
              </app-display-icon>
            </app-shared-button>
            <app-shared-button (click)="showFormatSidebar = false" colour="clear">
              <app-display-icon
                [colour]="iconColourEnum.white"
                [icon]="icons.close">
              </app-display-icon>
            </app-shared-button>
          </div>
        </div>
        <!-- Text-specific controls - only shown for non-image items -->
        @if (selectedItem.type !== 'image') {
          <div class="format-controls">
            <div class="form-field improved-form-field">
              <label for="font-size-input">Font Size</label>
              <input
                (ngModelChange)="drawGrid(); drawItems()"
                [(ngModel)]="selectedItem.fontSize"
                id="font-size-input"
                type="number">
            </div>
            <!-- Text Format Buttons -->
            <div class="button-group">
              <app-shared-button
                (click)="setFontWeight(selectedItem.fontWeight === 'bold' ? 'normal' : 'bold')"
                [colour]="selectedItem.fontWeight === 'bold' ? 'secondary' : 'clear'"
                [fill]="selectedItem.fontWeight === 'bold'">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.formatBold"
                  [tooltip]="'Bold'">
                </app-display-icon>
              </app-shared-button>
              <app-shared-button
                (click)="setFontStyle(selectedItem.fontStyle === 'italic' ? 'normal' : 'italic')"
                [colour]="selectedItem.fontStyle === 'italic' ? 'secondary' : 'clear'"
                [fill]="selectedItem.fontStyle === 'italic'">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.formatItalic"
                  [tooltip]="'Italics'">
                </app-display-icon>
              </app-shared-button>
            </div>
            <!-- Text Alignment Buttons -->
            <div class="button-group">
              <app-shared-button
                (click)="setTextAlign('left')"
                [colour]="selectedItem.textAlign === 'left' ? 'secondary' : 'clear'"
                [fill]="selectedItem.textAlign === 'left'">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.formatAlignLeft"
                  [tooltip]="'Text Align Left'">
                </app-display-icon>
              </app-shared-button>
              <app-shared-button
                (click)="setTextAlign('center')"
                [colour]="selectedItem.textAlign === 'center' ? 'secondary' : 'clear'"
                [fill]="selectedItem.textAlign === 'center'">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.formatAlignCenter"
                  [tooltip]="'Text Align Center'">
                </app-display-icon>
              </app-shared-button>
              <app-shared-button
                (click)="setTextAlign('right')"
                [class.selected]="selectedItem.textAlign === 'right'"
                [colour]="selectedItem.textAlign === 'right' ? 'secondary' : 'clear'"
                [fill]="selectedItem.textAlign === 'right'">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.formatAlignRight"
                  [tooltip]="'Text Align Right'">
                </app-display-icon>
              </app-shared-button>
            </div>
            <!-- Vertical and Horizontal Alignment Buttons -->
            <div class="button-group format-buttons">
              <app-shared-button (click)="alignHorizontally()" colour="clear">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.alignHorizontalCenter"
                  [tooltip]="'Align Horizontal'">
                </app-display-icon>
              </app-shared-button>
              <app-shared-button (click)="alignVertically()" colour="clear">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.alignVerticalCenter"
                  [tooltip]="'Align Vertical'">
                </app-display-icon>
              </app-shared-button>
            </div>
            <div class="form-field improved-form-field">
              <label for="overflow-select">Overflow Behavior</label>
              <select
                (change)="onOverflowSelectionChange()"
                (click)="$event.stopPropagation()"
                [(ngModel)]="selectedItem.overflow"
                class="overflow-select"
                id="overflow-select">
                <option value="wrap">Wrap</option>
                <option value="shrink">Auto-shrink</option>
              </select>
            </div>
          </div>
        }
        @if (selectedItem.type === 'image') {
          <div class="format-controls">
            <div class="button-group format-buttons">
              <app-shared-button (click)="alignHorizontally()" colour="clear">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.alignHorizontalCenter"
                  [tooltip]="'Align Horizontal'">
                </app-display-icon>
              </app-shared-button>
              <app-shared-button (click)="alignVertically()" colour="clear">
                <app-display-icon
                  [colour]="iconColourEnum.white"
                  [icon]="icons.alignVerticalCenter"
                  [tooltip]="'Align Vertical'">
                </app-display-icon>
              </app-shared-button>
            </div>
          </div>
        }
      </div>
    }
  </div>
</div>
